import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah } from "@/lib/constants";
import { NO_FINANCE_ROLES } from "@/lib/phaseCards";
import { Plate } from "@/components/StatusBadge";
import {
  ChevronRight, X, Search, Inbox, AlertTriangle, RefreshCw, Car, Wallet, Boxes, Factory,
  ClipboardList, FileText, LogIn, LogOut, Receipt, HandCoins, PackageSearch, Truck, Recycle, Wrench, SprayCan, CheckCircle2,
  ShieldAlert, Scale, MapPin,
} from "lucide-react";

const MGMT_ROLES = ["Owner", "Admin", "GM", "Branch Manager"];

function SecurityPanel() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/security/dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === false) return null;
  const diff = d ? (Number(d.security_in_today || 0) - Number(d.admin_in_today || 0)) : 0;
  const cards = [
    { key: "in", label: "Unit Masuk", value: d?.security_in_today, icon: LogIn, tone: "emerald", to: "/security/log" },
    { key: "out", label: "Unit Keluar", value: d?.security_out_today, icon: LogOut, tone: "blue", to: "/security/log" },
    { key: "diff", label: "Selisih Security vs Admin", value: d ? Math.abs(diff) : "—", icon: Scale, tone: diff !== 0 ? "amber" : "slate", to: "/security/recon" },
    { key: "critical", label: "Temuan Kritis", value: d?.reconcile_critical, icon: AlertTriangle, tone: (d?.reconcile_critical) ? "rose" : "slate", to: "/security/recon" },
    { key: "stay", label: "Unit Masih di Lokasi", value: d?.open_stay, icon: MapPin, tone: "cyan", to: "/security/log" },
    { key: "unresolved", label: "Rekonsiliasi Belum Selesai", value: d?.reconcile_unresolved, icon: ClipboardList, tone: (d?.reconcile_unresolved) ? "amber" : "slate", to: "/security/recon" },
  ];
  const TONE = {
    emerald: "text-emerald-300 bg-emerald-500/12 ring-emerald-400/30",
    blue: "text-blue-300 bg-blue-500/12 ring-blue-400/30",
    amber: "text-amber-300 bg-amber-500/12 ring-amber-400/30",
    rose: "text-rose-300 bg-rose-500/12 ring-rose-400/30",
    cyan: "text-cyan-300 bg-cyan-500/12 ring-cyan-400/30",
    slate: "text-slate-300 bg-white/5 ring-white/10",
  };
  return (
    <div className="ti-dcard relative mt-3 overflow-hidden" data-testid="owner-security-panel">
      <span className="absolute inset-y-0 left-0 w-1 bg-rose-500" />
      <div className="flex items-center gap-2 border-b border-white/8 px-3 py-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-rose-500/12 text-rose-300 ring-1 ring-inset ring-rose-400/30" style={{ boxShadow: "0 0 10px rgba(244,63,94,0.38)" }}><ShieldAlert size={13} /></span>
        <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-white">Security</h3>
        <span className="ml-auto text-[10px] text-[#7E91AC]">Klik kartu untuk detail</span>
      </div>
      <div className="grid grid-cols-2 gap-2 p-2.5 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <button key={c.key} data-testid={`sec-card-${c.key}`} onClick={() => navigate(c.to)} className="group flex min-w-0 flex-col items-start gap-1.5 rounded-lg border border-white/8 bg-white/[0.03] p-2.5 text-left transition-colors hover:border-blue-400/30 hover:bg-white/5">
            <span className={`flex h-7 w-7 items-center justify-center rounded-md ring-1 ring-inset ${TONE[c.tone]}`}><c.icon size={14} /></span>
            <span className="text-[10px] uppercase leading-tight tracking-wider text-[#7E91AC]">{c.label}</span>
            <span className="text-lg font-extrabold text-white">{c.value ?? "—"}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const PANELS = [
  { key: "unit", title: "UNIT", icon: Car, tone: "blue" },
  { key: "finance", title: "FINANCE", icon: Wallet, tone: "green" },
  { key: "sparepart", title: "SPAREPART", icon: Boxes, tone: "purple" },
  { key: "produksi", title: "PRODUKSI", icon: Factory, tone: "cyan" },
];
const T = {
  blue: { bar: "bg-blue-500", ic: "text-blue-300", bg: "bg-blue-500/12", ring: "ring-blue-400/30", glow: "rgba(22,132,255,0.38)" },
  green: { bar: "bg-emerald-500", ic: "text-emerald-300", bg: "bg-emerald-500/12", ring: "ring-emerald-400/30", glow: "rgba(16,185,129,0.38)" },
  purple: { bar: "bg-violet-500", ic: "text-violet-300", bg: "bg-violet-500/12", ring: "ring-violet-400/30", glow: "rgba(139,92,246,0.38)" },
  cyan: { bar: "bg-cyan-500", ic: "text-cyan-300", bg: "bg-cyan-500/12", ring: "ring-cyan-400/30", glow: "rgba(34,211,238,0.38)" },
};
const ROW_ICON = {
  estimasi: ClipboardList, wo_in: FileText, unit_in: LogIn, unit_out: LogOut, invoice: Receipt,
  cash_in: Wallet, cash_out: Receipt, hutang: HandCoins, piutang: Wallet,
  parts_po: PackageSearch, parts_datang: Truck, parts_terpasang: Wrench, salvage: Recycle,
  masuk_produksi: Factory, siap_cat: SprayCan, unit_jadi: CheckCircle2,
};

function KpiRow({ row, tone, canFin, onClick }) {
  const t = T[tone];
  const Icon = ROW_ICON[row.key] || ClipboardList;
  const showAmt = canFin && row.amount != null;
  return (
    <button data-testid={`kpi-row-${row.key}`} onClick={onClick} className="group flex h-10 w-full items-center gap-2.5 border-b border-white/5 px-2.5 text-left transition-colors last:border-0 hover:bg-white/5">
      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ring-1 ring-inset ${t.bg} ${t.ic} ${t.ring}`}><Icon size={13} /></span>
      <span className="w-[86px] shrink-0 truncate text-[12px] font-semibold text-white sm:w-[110px]">{row.label}</span>
      <span className="shrink-0 text-[12px] font-bold text-white">{row.count}</span>
      <span className="min-w-0 flex-1 truncate text-right text-[11px] text-[#AAB8CC]">
        {showAmt ? <b className="text-slate-200">{formatRupiah(row.amount)}</b> : null}
        {row.extra ? <span className={showAmt ? "ml-1.5" : ""}>{row.extra}</span> : null}
        {!showAmt && !row.extra && canFin && row.amount == null ? "" : null}
      </span>
      {row.warn > 0 && <span className="flex shrink-0 items-center gap-0.5 rounded-full border border-rose-400/30 bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-bold text-rose-300"><AlertTriangle size={10} />{row.warn}</span>}
      <ChevronRight size={14} className="shrink-0 text-[#4a6285] transition-colors group-hover:text-blue-300" />
    </button>
  );
}

function Panel({ def, rows, footer, canFin, onRow }) {
  const t = T[def.tone];
  return (
    <div className="ti-dcard relative overflow-hidden">
      <span className={`absolute inset-y-0 left-0 w-1 ${t.bar}`} />
      <div className="flex items-center gap-2 border-b border-white/8 px-3 py-2">
        <span className={`flex h-6 w-6 items-center justify-center rounded-md ring-1 ring-inset ${t.bg} ${t.ic} ${t.ring}`} style={{ boxShadow: `0 0 10px ${t.glow}` }}><def.icon size={13} /></span>
        <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-white">{def.title}</h3>
      </div>
      <div>{(rows || []).map((r) => <KpiRow key={r.key} row={r} tone={def.tone} canFin={canFin} onClick={() => onRow(r)} />)}</div>
      {footer && (
        <div className="flex items-center justify-between border-t border-white/10 bg-white/[0.02] px-3 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#7E91AC]">{footer.label}</span>
          <span className="text-[12px] font-bold text-white">{footer.count} Unit{canFin ? ` · ${formatRupiah(footer.amount)}` : ""}</span>
        </div>
      )}
    </div>
  );
}

function Detail({ kpi, label, range, canFin, onClose }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    setRows(null);
    api.get("/dashboard/panels/detail", { params: { kpi, from: range.from, to: range.to } }).then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  }, [kpi, range.from, range.to]);
  const isCash = kpi === "cash_in" || kpi === "cash_out";
  const isPart = ["parts_po", "parts_datang", "parts_terpasang", "salvage"].includes(kpi);
  const visible = useMemo(() => (rows || []).filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return `${r.license_plate || ""} ${r.claim_no || ""} ${r.wo_number || ""} ${r.invoice_number || ""} ${r.customer || ""} ${r.part_name || ""} ${r.supplier || ""} ${r.desc || ""}`.toLowerCase().includes(s);
  }), [rows, q]);

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="kpi-detail">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="ti-dark relative flex h-full w-full flex-col border-l border-[#29466D] shadow-2xl sm:w-[520px]">
        <div className="flex items-center justify-between border-b border-[#1b3357] px-4 py-3">
          <div>
            <h2 className="font-heading text-base font-bold text-white">{label}</h2>
            <p className="text-[11px] text-[#AAB8CC]">{visible.length} item · {range.from === range.to ? range.from : `${range.from} → ${range.to}`}</p>
          </div>
          <button data-testid="kpi-detail-close" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2] hover:text-white"><X size={16} /></button>
        </div>
        <div className="border-b border-[#1b3357] px-4 py-2.5">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7E91AC]" />
            <input data-testid="kpi-detail-search" placeholder="Cari Nopol / Claim / WO / Invoice / Customer…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-full rounded-lg border border-[#29466D] bg-[#0a2038]/70 pl-9 pr-3 text-sm text-white placeholder:text-[#7E91AC] outline-none focus:border-[#1684FF]" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {rows === null ? <p className="py-10 text-center text-sm text-[#7E91AC]">Memuat…</p> : visible.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12"><Inbox size={24} className="text-[#4a6285]" /><p className="text-sm text-[#AAB8CC]">Tidak ada data pada periode ini.</p></div>
          ) : (
            <div className="space-y-2">
              {visible.map((r, i) => (
                <div key={i} data-testid={`kpi-detail-row-${i}`} className="ti-dcard p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    {isCash ? <span className="font-mono text-[12px] font-bold text-white">{r.txn_no || "-"}</span> : <Plate value={r.license_plate || "—"} />}
                    <span className="text-[11px] text-[#7E91AC]">{r.date || "-"}{r.aging ? ` · ${r.aging}h` : ""}</span>
                  </div>
                  <div className="mt-1 truncate text-[12px] font-semibold text-white">{isCash ? (r.desc || "-") : isPart ? (r.part_name || "-") : (r.vehicle || "-")}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[#AAB8CC]">
                    {!isCash && r.customer && <span>{r.customer}</span>}
                    {r.claim_no && <span>· {r.claim_no}</span>}
                    {r.wo_number && <span>· WO {r.wo_number}</span>}
                    {r.spk_number && <span>· SPK {r.spk_number}</span>}
                    {r.invoice_number && <span>· Inv {r.invoice_number}</span>}
                    {r.supplier && <span>· {r.supplier}</span>}
                    {isCash && <span>· {r.kind === "kecil" ? "Kas Kecil" : "Kas Besar"}</span>}
                    {r.insurance && !isCash && !isPart && <span>· {r.insurance}</span>}
                    {r.status && <span>· {r.status}</span>}
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    {canFin && (r.amount != null && r.amount !== 0) ? <span className="text-[12px] font-bold text-slate-200">{formatRupiah(r.amount)}</span> : <span />}
                    {r.claim_id && <button data-testid={`kpi-detail-open-${i}`} onClick={() => { onClose(); navigate(`/claims/${r.claim_id}`); }} className="text-[11px] font-semibold text-blue-300 hover:underline">Buka detail unit →</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DashboardPanels({ title, subtitle, range, headerRight, onRefresh }) {
  const [panels, setPanels] = useState(null);
  const [err, setErr] = useState(false);
  const [detail, setDetail] = useState(null);
  const { user } = useAuth();
  const canFin = !NO_FINANCE_ROLES.includes(user?.role_name || "");
  const isMgmt = user?.role === "admin" || user?.role === "root" || MGMT_ROLES.includes(user?.role_name || "");

  const load = useCallback(() => {
    setErr(false);
    api.get("/dashboard/panels", { params: { from: range.from, to: range.to } }).then((r) => setPanels(r.data.panels)).catch(() => setErr(true));
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const rowLabel = (r) => r.label;

  return (
    <div className="ti-dark -m-3 -mb-24 min-h-[calc(100vh-3rem)] px-4 py-4 sm:-m-5 sm:px-6 lg:-mb-5 lg:px-8 lg:py-5">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="font-heading text-lg font-extrabold tracking-tight text-white sm:text-xl">{title}</h1>
            <p className="mt-0.5 text-[11px] text-[#AAB8CC]">{subtitle}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {headerRight}
            <button data-testid="dash-refresh" onClick={() => { load(); onRefresh && onRefresh(); }} className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2] hover:text-white" title="Refresh"><RefreshCw size={14} /></button>
          </div>
        </div>

        {err && <div data-testid="dash-error" className="mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-300">Gagal memuat data. Coba Refresh.</div>}

        {isMgmt && <SecurityPanel />}

        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          {PANELS.map((def) => (
            <Panel key={def.key} def={def} rows={panels?.[def.key]} footer={def.key === "unit" ? panels?.unit_footer : null} canFin={canFin} onRow={(r) => setDetail({ kpi: r.key, label: rowLabel(r) })} />
          ))}
        </div>
        {!panels && !err && <p className="mt-4 text-center text-xs text-[#7E91AC]">Memuat data…</p>}
      </div>

      {detail && <Detail kpi={detail.kpi} label={detail.label} range={range} canFin={canFin} onClose={() => setDetail(null)} />}
    </div>
  );
}
